import React, { FC, memo, useCallback, useEffect, useState } from 'react';

import testIds from '@smart-tv/e2e/shared/variables';

import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import Typography from '@smart-tv/components/typography';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import FavoritesHomeChannels from './favorites-home-channels';
import FavoritesHomeCreate from './favorites-home-create';
import FavoritesHomeList from './favorites-home-list';
import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { FavoritesScreen } from '@smart-tv/contexts/favorites-edit-context/favorites-edit-context';
import styles from '../favorites.module.scss';
import Button from '@smart-tv/components/button/button';

export enum FailedActionSource {
  SelectFavoritesList = 'SELECT_FAVORITES_LIST',
  CreateFavoritesList = 'CREATE_FAVORITES_LIST',
  EditFavoritesList = 'EDIT_FAVORITES_LIST',
  DeleteFavoritesList = 'DELETE_FAVORITES_LIST'
}

export const FavoritesHome: FC = () => {
  const { getFavoritesLists } = useFavoritesLists();
  const { handleSetInitialFavoriteList, handleSelectScreen, handleClose } = useFavoritesEdit();

  const { ref, focusKey } = useFocusable({ isFocusBoundary: true });
  const [homeListFocus, setHomeListFocus] = useState(true);
  const { checkAndUpdateNetwork, setIsOffline, isOffline } = useNetwork();
  const [createButtonNetworkError, setCreateButtonNetworkError] = useState(false);

  useEffect(() => {
    getFavoritesLists();
  }, [getFavoritesLists]);

  const handleCreateFavoritesList = useCallback(async () => {
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      handleSetInitialFavoriteList(-1);
      handleSelectScreen(FavoritesScreen.Edit);
      setCreateButtonNetworkError(false);
    } else {
      setCreateButtonNetworkError(true);
    }
  }, [checkAndUpdateNetwork, handleSetInitialFavoriteList, handleSelectScreen]);

  const closeNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
    setCreateButtonNetworkError(false);
  }, [setIsOffline]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={styles['favorites-home-layout']}
        ref={ref}
        data-testid={testIds.favorites.home.root}
      >
        {createButtonNetworkError && (
          <NetworkErrorDialog
            onTryAgain={() => handleCreateFavoritesList()}
            onClose={closeNetworkErrorDialogAction}
          />
        )}
        <Typography
          className={styles['favorites-home-root--title']}
          variant="h2"
          data-testid={testIds.favorites.home.title}
        >
          Rediger kanallister
        </Typography>
        <FavoritesHomeList
          isFocused={homeListFocus}
          onFocus={() => setHomeListFocus(true)}
          onBlur={() => !createButtonNetworkError && !isOffline && setHomeListFocus(false)}
        />
        <FavoritesHomeCreate onCreateClick={handleCreateFavoritesList} />
        {IS_BROWSER && (
          <Button
            className={styles['favorites-home-root--back-btn']}
            onClick={handleClose}
            data-testid={testIds.favorites.home.itemActions.backButton}
          >
            Tilbage
          </Button>
        )}
      </div>
      <FavoritesHomeChannels />
    </FocusContext.Provider>
  );
};

export default memo(FavoritesHome);
